import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PortfolioProfileTabs, PortfolioProfileView } from "components/portfolioProfile";
import api from "services/http.service";
import { getProfile } from "slices/user";
import { useDispatch, useSelector } from "store";
import { accentRedContainedButtonSx, agenticPageSx } from "styles/main_style";
import type { PortfolioPageProps, Profile, UpdateProfileRequest } from "models";
import { AGENT_PATH, ONBOARD_PATH } from "utils/navigation";
import { PROFILE_SAVED_EVENT } from "utils/publishGeneratedProfile";
import MarketingLayout from "components/layout/MarketingLayout";

const EMPTY_PROFILE: Profile = {
  username: "",
  name: "My Profile",
  summary: "",
  video: "",
  isViewable: false,
  skills: [],
  projectCategories: [{ title: "", items: [{ name: "", description: "" }] }],
  contact: { email: "", phone: "" },
};

export default function PortfolioPage({ onLogout }: PortfolioPageProps) {
  const dispatch = useDispatch();
  const { profile, error: loadError, isLoading } = useSelector((state) => state.user);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    void dispatch(getProfile(onLogout) as any);
    const refresh = () => {
      void dispatch(getProfile(onLogout) as any);
    };
    window.addEventListener(PROFILE_SAVED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_SAVED_EVENT, refresh);
  }, [dispatch, onLogout]);

  const handleSaveProfile = async (nextProfile: Profile, mode: "create" | "update") => {
    const payload: UpdateProfileRequest = {
      summary: nextProfile.summary,
      video: nextProfile.video,
      isViewable: nextProfile.isViewable,
      skills: nextProfile.skills,
      contact: nextProfile.contact,
      projectCategories: nextProfile.projectCategories,
    };
    try {
      if (mode === "create") {
        await api.post("/profiledetails", payload);
      } else {
        await api.put("/profiledetails", payload);
      }
    } catch (error: any) {
      if (error?.response?.status === 404 && mode === "update") {
        await api.post("/profiledetails", payload);
      } else {
        throw error;
      }
    }
    await dispatch(getProfile(onLogout) as any);
  };

  const handleDeleteAccount = async () => {
    await api.delete("/account");
    onLogout();
  };

  if (!profile && isLoading) {
    return (
      <Container sx={agenticPageSx.container}>
        <Box sx={agenticPageSx.loadingState}>
          Loading...
        </Box>
      </Container>
    );
  }

  if (!profile && loadError) {
    return (
      <Container sx={agenticPageSx.container}>
        <Box sx={agenticPageSx.loadingState}>{loadError ?? "Unable to load profile details."}</Box>
      </Container>
    );
  }

  const activeProfile = profile ?? EMPTY_PROFILE;
  const hasGeneratedContent = Boolean(
    activeProfile.summary.trim() ||
      activeProfile.skills.length ||
      activeProfile.projectCategories.some((category) =>
        category.items.some((item) => item.name.trim())
      )
  );
  const isCreateMode = !profile;

  return (
    <MarketingLayout mainSx={agenticPageSx.embeddedMain} embedded>
      <Stack sx={agenticPageSx.stackSections}>
        <Box sx={agenticPageSx.panelBody}>
          <Box component="header" sx={agenticPageSx.headerRow}>
            <Box sx={agenticPageSx.headerLeft}>
              <Typography component="p" sx={agenticPageSx.pageKindLabel}>
                Portfolio
              </Typography>
              <Typography
                component="h1"
                data-testid="profile-name"
                sx={agenticPageSx.profileName}
              >
                {activeProfile.name}
              </Typography>
            </Box>
            <Box sx={agenticPageSx.headerActionsRow}>
              {!isEditing ? (
                <>
                  {!hasGeneratedContent ? (
                    <>
                      <Button
                        component={RouterLink}
                        to={AGENT_PATH}
                        variant="contained"
                        sx={[agenticPageSx.headerOutlinedButton, accentRedContainedButtonSx]}
                      >
                        Agentic AI
                      </Button>
                      <Button
                        component={RouterLink}
                        to={ONBOARD_PATH}
                        variant="contained"
                        sx={[agenticPageSx.headerOutlinedButton, accentRedContainedButtonSx]}
                      >
                        AI Profile Builder
                      </Button>
                    </>
                  ) : null}
                  <Button variant="outlined" onClick={() => setIsEditing(true)} sx={agenticPageSx.headerOutlinedButton}>
                    {isCreateMode ? "Create portfolio" : "Edit portfolio"}
                  </Button>
                </>
              ) : (
                <Button variant="outlined" onClick={() => setIsEditing(false)} sx={agenticPageSx.headerOutlinedButton}>
                  Back to view
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {isEditing ? (
          <PortfolioProfileTabs
            profile={activeProfile}
            onSave={handleSaveProfile}
            onDeleteAccount={handleDeleteAccount}
            initialMode="edit"
            saveMode={isCreateMode ? "create" : "update"}
          />
        ) : (
          <PortfolioProfileView profile={activeProfile} />
        )}
      </Stack>
    </MarketingLayout>
  );
}
